import React, { useState } from 'react';
import axios from 'axios';

const BLUE = '#00388A';

const PSQIForm = ({ userHash, onComplete }) => {
  const [formData, setFormData] = useState({
    bedTime: '',
    latencyMin: '',
    getUpTime: '',
    actualSleep: '',
    disturbances: {},
    overallQuality: null
  });

  const disturbanceList = [
    { id: '5a', text: "Cannot get to sleep within 30 minutes" },
    { id: '5b', text: "Wake up in the middle of the night or early morning" },
    { id: '5c', text: "Have to get up to use the bathroom" },
    { id: '5d', text: "Cannot breathe comfortably" },
    { id: '5e', text: "Cough or snore loudly" },
    { id: '5f', text: "Feel too cold" },
    { id: '5g', text: "Feel too hot" },
    { id: '5h', text: "Have bad dreams/nightmares" },
    { id: '5i', text: "Have pain" }
  ];

  const freqOptions = [
    "Not during the past month",
    "Less than once a week",
    "Once or twice a week",
    "Three or more times a week"
  ];

  // Hourly options for bed time and get up time
  const hourlyTimeOptions = [];
  for (let h = 0; h < 24; h++) {
    const hour = h.toString().padStart(2, '0');
    hourlyTimeOptions.push(`${hour}:00`);
  }

  const latencyOptions = [5, 10, 15, 20, 30, 45, 60, 90, 120];

  const sleepOptions = [];
  for (let h = 1; h <= 12; h++) {
    sleepOptions.push(h);
    if (h < 12) sleepOptions.push(h + 0.5);
  }

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleDisturbance = (id, val) => {
    setFormData({
      ...formData,
      disturbances: { ...formData.disturbances, [id]: val }
    });
  };

  const handleSubmit = async () => {
    const payload = {
      student_id: userHash || "STUDENT_TEST_001",
      bedTime: formData.bedTime || "00:00",
      latencyMin: parseInt(formData.latencyMin) || 0,
      getUpTime: formData.getUpTime || "00:00",
      actualSleep: parseFloat(formData.actualSleep) || 0,
      disturbances: formData.disturbances
    };

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const res = await axios.post(`${baseUrl}/api/assessment/psqi`, payload);
      onComplete(res.data);
    } catch (err) {
      console.error("Submission Error:", err.response?.data);
      if (err.response?.status === 409) {
        // questionario ancora bloccato lato server (finestra di 28 giorni)
        alert(err.response.data?.detail || "The questionnaires are not available yet.");
      } else {
        alert("Something went wrong. Please try again later.");
      }
    }
  };

  return (
    <div style={formCardStyle}>
      <header style={{ marginBottom: '30px', textAlign: 'center' }}>
        <p style={stepLabelStyle}>Questionnaire 2/3</p>
        <p style={introTextStyle}>Think about your sleep habits in the last month. Please answer the following questions:</p>
      </header>

      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'grid', gap: '25px' }}>
          <div>
            <label style={labelStyle}>When have you usually gone to bed at night?</label>
            <select
              value={formData.bedTime}
              onChange={(e) => handleChange('bedTime', e.target.value)}
              style={selectStyle}
            >
              <option value="">Select time...</option>
              {hourlyTimeOptions.map(t => (
                <option key={`bed-${t}`} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>How many minutes has it usually taken you to fall asleep?</label>
            <select
              value={formData.latencyMin}
              onChange={(e) => handleChange('latencyMin', e.target.value)}
              style={selectStyle}
            >
              <option value="">Select...</option>
              {latencyOptions.map(m => (
                <option key={`lat-${m}`} value={m}>{m} minutes</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>When have you usually gotten up in the morning?</label>
            <select
              value={formData.getUpTime}
              onChange={(e) => handleChange('getUpTime', e.target.value)}
              style={selectStyle}
            >
              <option value="">Select time...</option>
              {hourlyTimeOptions.map(t => (
                <option key={`up-${t}`} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>How many hours of actual sleep did you get at night?</label>
            <select
              value={formData.actualSleep}
              onChange={(e) => handleChange('actualSleep', e.target.value)}
              style={selectStyle}
            >
              <option value="">Select...</option>
              {sleepOptions.map(h => (
                <option key={`sleep-${h}`} value={h}>{h} hours</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '40px' }}>
        <p style={{ fontSize: '15px', color: '#444', marginBottom: '20px' }}>During the past month, how often have you had trouble sleeping because you...</p>

        {disturbanceList.map((item) => (
          <div key={item.id} style={disturbanceRowStyle}>
            <p style={disturbanceTextStyle}>{item.text}</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {freqOptions.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleDisturbance(item.id, idx)}
                  style={{
                    ...btnToggleStyle,
                    backgroundColor: formData.disturbances[item.id] === idx ? BLUE : 'transparent',
                    color: formData.disturbances[item.id] === idx ? 'white' : '#555',
                    borderColor: formData.disturbances[item.id] === idx ? BLUE : '#DDD',
                  }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <button onClick={handleSubmit} style={submitButtonStyle}>Continue</button>
      </div>
    </div>
  );
};

const stepLabelStyle = { fontSize: '13px', color: BLUE, fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '15px' };
const formCardStyle = { maxWidth: '700px', width: '100%', backgroundColor: '#FFFFFF', padding: '50px 40px', borderRadius: '20px', boxShadow: '0 2px 15px rgba(0,0,0,0.03)' };
const introTextStyle = { fontSize: '18px', lineHeight: '1.6', color: '#444', marginBottom: '20px', textAlign: 'center' };
const labelStyle = { fontSize: '15px', fontWeight: '500', color: '#333', display: 'block', marginBottom: '8px' };
const selectStyle = { display: 'block', width: '100%', padding: '12px', border: '1px solid #EEE', borderRadius: '8px', fontSize: '15px', color: '#333', backgroundColor: '#FAFAFA', cursor: 'pointer', outline: 'none' };
const disturbanceRowStyle = { marginBottom: '15px', padding: '15px', backgroundColor: '#FAFAFA', borderRadius: '12px' };
const disturbanceTextStyle = { fontSize: '14px', fontWeight: '500', marginBottom: '12px', color: '#333' };
const btnToggleStyle = { padding: '8px 14px', fontSize: '12px', border: '1px solid', cursor: 'pointer', borderRadius: '20px', transition: 'all 0.2s ease', fontWeight: '500' };
const submitButtonStyle = { backgroundColor: BLUE, color: 'white', padding: '15px 50px', border: 'none', borderRadius: '50px', fontSize: '16px', fontWeight: '600', letterSpacing: '0.5px', transition: 'all 0.3s ease', cursor: 'pointer' };

export default PSQIForm;